import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/AuthContext.jsx';
import { useLive } from '../../lib/hooks.js';
import {
  Loading,
  Empty,
  Stat,
  StatusPill,
  Avatar,
  Stars,
  useToast,
  PageHeader,
  LiveBadge,
} from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { currency, formatDayLabel, timeRange, todayISO, STATUS_LABEL, formatTime } from '../../lib/format.js';

export default function NurseDashboard() {
  const { user, nurseProfile } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(0);

  const fetcher = useCallback(
    () => Promise.all([api.get('/api/bookings'), api.get('/api/nurses/me/earnings')]),
    []
  );

  // The dashboard is the nurse's live inbox — new requests land without a reload.
  const { data, setData, loading, stale, refresh } = useLive(fetcher, {
    interval: 15000,
    onChange: ([next], [prev]) => {
      const before = new Set(prev.filter((b) => b.status === 'pending').map((b) => b.id));
      const arrived = next.filter((b) => b.status === 'pending' && !before.has(b.id));
      if (arrived.length) {
        toast.success(
          arrived.length === 1
            ? `New request from ${arrived[0].patient_name} for ${arrived[0].service_name}.`
            : `${arrived.length} new booking requests.`
        );
        if (navigator.vibrate) navigator.vibrate([40, 60, 40]);
      }
    },
  });

  if ((loading && !data) || !nurseProfile) return <Loading rows={3} />;
  if (!data)
    return (
      <Empty art="offline" title="Could not load your dashboard">
        Check your connection and try again.
      </Empty>
    );

  const [bookings, earnings] = data;

  /** Accept/decline straight from the dashboard, no page change needed. */
  const respond = async (booking, action) => {
    setBusy(booking.id);
    try {
      const updated = await api.post(`/api/bookings/${booking.id}/respond`, { action });
      setData(([list, e]) => [list.map((b) => (b.id === booking.id ? { ...b, ...updated } : b)), e]);
      toast.success(
        action === 'accept'
          ? `Confirmed — ${booking.patient_name} on ${formatDayLabel(booking.date)}.`
          : 'Request declined.'
      );
    } catch (err) {
      toast.error(err);
      refresh();
    } finally {
      setBusy(0);
    }
  };

  const today = todayISO();
  const pending = bookings.filter((b) => b.status === 'pending');
  const todays = bookings.filter((b) => b.status === 'accepted' && b.date === today);
  const upcoming = bookings.filter((b) => b.status === 'accepted' && b.date > today).slice(0, 5);
  const verified = nurseProfile.verification_status === 'verified';

  return (
    <>
      <PageHeader
        title={user.name}
        avatar={<Avatar name={user.name} avatar={user.avatar} size="lg" ring />}
        subtitle={nurseProfile.headline || 'Home nurse'}
      >
        <Link to="/nurse/availability" className="btn btn-primary">
          <Icon name="calendar" /> Manage availability
        </Link>
      </PageHeader>

      <div className="row-tight mb-3" style={{ flexWrap: 'wrap' }}>
        <Stars value={nurseProfile.rating_avg} count={nurseProfile.rating_count} />
        <span className="dim">·</span>
        <span className="small muted">{nurseProfile.jobs_completed} visits completed</span>
      </div>

      {/* --------------------------------------------------- Status banners */}
      {!verified && (
        <div className={`banner banner-${nurseProfile.verification_status === 'rejected' ? 'danger' : 'warning'}`}>
          <Icon name={nurseProfile.verification_status === 'rejected' ? 'warning' : 'hourglass'} />
          <span className="grow">
            {nurseProfile.verification_status === 'rejected' ? (
              <>
                <strong>Verification unsuccessful.</strong>{' '}
                {nurseProfile.rejection_reason || 'Please update your profile and re-submit.'}
              </>
            ) : (
              <>
                <strong>Verification pending.</strong> Your profile is hidden from patients until an
                administrator approves your licence and certificates.
              </>
            )}
          </span>
          <Link to="/nurse/profile" className="btn btn-sm">
            Update profile
          </Link>
        </div>
      )}

      {!nurseProfile.is_available && verified && (
        <div className="banner banner-info">
          <Icon name="bell-off" />
          <span className="grow">
            You are marked <strong>unavailable</strong> — patients cannot find or book you.
          </span>
          <Link to="/nurse/profile" className="btn btn-sm">
            Go online
          </Link>
        </div>
      )}

      {/* --------------------------------------------------------- Stats */}
      <div className="grid grid-4 mb-3">
        <Stat
          label="New requests"
          value={pending.length}
          sub="Awaiting your response"
          icon="inbox"
          tone={pending.length ? 'warning' : undefined}
        />
        <Stat
          label="Visits today"
          value={todays.length}
          sub={todays.length ? timeRange(todays[0].start_time, todays[0].end_time) : 'Nothing scheduled'}
          icon="calendar"
        />
        <Stat
          label="Total earned"
          value={currency(earnings?.earned || 0)}
          sub={`${earnings?.completed_jobs || 0} completed visits`}
          icon="wallet"
        />
        <Stat
          label="Rating"
          value={(nurseProfile.rating_avg || 0).toFixed(1)}
          sub={`${nurseProfile.rating_count} reviews`}
          icon="star"
        />
      </div>

      <div className="detail-layout">
        <div className="stack">
          {/* ------------------------------------------------- Requests */}
          <section className="card">
            <div className="card-header">
              <h2>Booking requests</h2>
              {pending.length > 0 && <span className="pill pill-warning">{pending.length} waiting</span>}
              <div className="spacer" />
              <LiveBadge stale={stale} />
            </div>
            {pending.length === 0 ? (
              <div className="card-body">
                <Empty art="inbox" title="No pending requests">
                  New booking requests from patients appear here the moment they arrive.
                </Empty>
              </div>
            ) : (
              pending.map((b) => (
                <div key={b.id} className="list-row just-updated" style={{ flexWrap: 'wrap' }}>
                  <span className="icon-tile" aria-hidden="true">
                    {b.service_icon || <Icon name="stethoscope" />}
                  </span>
                  <Link to={`/nurse/bookings/${b.id}`} className="grow" style={{ color: 'inherit', minWidth: 180 }}>
                    <div className="list-title truncate">
                      {b.service_name} · {b.patient_name}
                    </div>
                    <div className="list-sub">
                      {formatDayLabel(b.date)} · {timeRange(b.start_time, b.end_time)} · {currency(b.amount)}
                    </div>
                    <div className="tiny dim truncate row-tight" style={{ marginTop: 2 }}>
                      <Icon name="map-pin" size={12} />
                      {b.address}
                    </div>
                  </Link>
                  <div className="row-tight">
                    <button className="btn btn-sm" disabled={busy === b.id} onClick={() => respond(b, 'decline')}>
                      Decline
                    </button>
                    <button className="btn btn-sm btn-success" disabled={busy === b.id} onClick={() => respond(b, 'accept')}>
                      {busy === b.id ? (
                        <span className="spinner" />
                      ) : (
                        <>
                          <Icon name="check" /> Accept
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </section>

          {/* --------------------------------------------------- Today */}
          <section className="card">
            <div className="card-header">
              <h2 className="grow">Today's schedule</h2>
              <span className="tiny dim">{formatDayLabel(today)}</span>
            </div>
            {todays.length === 0 ? (
              <div className="card-body">
                <Empty art="empty-bookings" title="Nothing scheduled today">
                  Confirmed visits for today will appear here with their timings.
                </Empty>
              </div>
            ) : (
              todays.map((b) => (
                <Link key={b.id} to={`/nurse/bookings/${b.id}`} className="list-row">
                  <div style={{ textAlign: 'center', minWidth: 68 }}>
                    <div className="bold tabular">{formatTime(b.start_time)}</div>
                    <div className="tiny dim">{b.hours} hr</div>
                  </div>
                  <div className="grow">
                    <div className="list-title truncate">
                      {b.patient_name} · {b.service_name}
                    </div>
                    <div className="list-sub truncate row-tight">
                      <Icon name="map-pin" size={13} />
                      {b.address}
                    </div>
                  </div>
                  <StatusPill status={b.status} label={STATUS_LABEL[b.status]} />
                  <span className="list-chevron">
                    <Icon name="chevron-right" />
                  </span>
                </Link>
              ))
            )}
          </section>
        </div>

        <aside className="stack sticky-aside">
          <section className="card">
            <div className="card-header">
              <h3 className="grow">Coming up</h3>
              <Link to="/nurse/bookings" className="small">
                All →
              </Link>
            </div>
            {upcoming.length === 0 ? (
              <div className="card-body">
                <p className="small dim">Nothing scheduled ahead.</p>
              </div>
            ) : (
              upcoming.map((b) => (
                <Link key={b.id} to={`/nurse/bookings/${b.id}`} className="list-row">
                  <div className="grow">
                    <div className="list-title small truncate">{b.patient_name}</div>
                    <div className="tiny dim">
                      {formatDayLabel(b.date)} · {timeRange(b.start_time, b.end_time)}
                    </div>
                  </div>
                  <span className="small bold tabular">{currency(b.amount)}</span>
                </Link>
              ))
            )}
          </section>

          <section className="card">
            <div className="card-header">
              <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
                <Icon name="wallet" size={16} />
              </span>
              <h3>This month</h3>
            </div>
            <div className="card-body stack-sm">
              <div className="row-between">
                <span className="small dim">Confirmed upcoming</span>
                <span className="bold tabular">{currency(earnings?.upcoming || 0)}</span>
              </div>
              <div className="row-between">
                <span className="small dim">Completed visits</span>
                <span className="bold tabular">{earnings?.completed_jobs || 0}</span>
              </div>
              <Link to="/nurse/earnings" className="btn btn-sm btn-block mt-1">
                View earnings
              </Link>
            </div>
          </section>
        </aside>
      </div>
    </>
  );
}
